import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useWindowShell } from './context'
import type { WindowNavigationState } from './navigation'
import type { WindowParams } from './types'
import { useWindowStack } from './useWindowStack'

/**
 * Вход в окно на каждом переходе — вызывается один раз каркасом приложения.
 *
 * Layout-эффект, а не обычный: работу ушедшего окна надо снять, ПОКА состояние
 * приложения ещё его; эффекты нового экрана идут позже. Вкладку переход НЕ
 * создаёт (карточка 274).
 */
export function useWindowEntry(): void {
  const shell = useWindowShell()
  const commands = shell.useCommands()
  const location = useLocation()
  const previous = useRef<string | null>(null)
  const navigationState = location.state as WindowNavigationState | null

  useLayoutEffect(() => {
    commands.enter({
      pathname: location.pathname,
      previous: previous.current,
      from: navigationState?.windowFrom !== undefined ? navigationState.windowFrom : previous.current,
      explicit: navigationState?.windowFrom !== undefined,
      back: navigationState?.windowBack === true,
      tabSaved: navigationState?.windowTabSaved === true,
    })
  }, [commands, location.pathname, navigationState])

  useEffect(() => {
    previous.current = `${location.pathname}${location.search}`
  }, [location.pathname, location.search])
}

/**
 * Открыть окно по имени и параметрам (rid, ключ). Вид выбирает правило (UI-RMK-FLOW, раздел 1), а не
 * открывающий: рабочее окно (пункт меню, вход или вкладка раздела) открывается обычным переходом на
 * свой адрес, как и страница раздела (`presentation: 'page'`) — «откуда пришли» ядро запоминает вместе с окнами
 * поверх экрана, которые были открыты;
 * диалоги и инструменты (ход оплаты, сверка, видоискатель) встают поверх экрана, как раньше.
 */
export function useOpenWindow(): (kind: string, params?: WindowParams) => void {
  const shell = useWindowShell()
  const { open } = useWindowStack()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  return useCallback(
    (kind, params) => {
      const reference = shell.refOf(kind, params)
      if (shell.parseRef(reference)?.onAddress === true) {
        const state: WindowNavigationState = { windowFrom: `${pathname}${search}` }
        void navigate(shell.pathOf(kind, params), { state })
        return
      }
      open(reference)
    },
    [navigate, open, pathname, search, shell],
  )
}
